/**
 * Client-side CSV serializer for downloading `message.data[]` without a backend round-trip.
 *
 * RFC-4180 escaping: fields containing `,`, `"`, or newline are wrapped in double quotes,
 * with any interior `"` doubled. A UTF-8 BOM is prepended so Excel reads the file as UTF-8.
 */

const UTF8_BOM = '\ufeff';

function escapeField(value: unknown): string {
  if (value === null || value === undefined) return '';
  let s: string;
  if (typeof value === 'string') {
    s = value;
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    s = String(value);
  } else {
    // Nested objects / arrays → JSON string (best-effort).
    try {
      s = JSON.stringify(value);
    } catch {
      s = String(value);
    }
  }
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export interface RowsToCsvOptions {
  columns?: string[];
}

export function rowsToCsv(
  rows: Record<string, unknown>[],
  options?: RowsToCsvOptions,
): string {
  if (!rows || rows.length === 0) return UTF8_BOM;
  const columns = options?.columns ?? Object.keys(rows[0]);
  const header = columns.map(escapeField).join(',');
  const body = rows.map((r) => columns.map((c) => escapeField(r[c])).join(',')).join('\n');
  return `${UTF8_BOM}${header}\n${body}\n`;
}

/**
 * Trigger a file download from a Blob or string.
 */
export function triggerDownload(content: string | Blob, filename: string): void {
  const blob =
    typeof content === 'string'
      ? new Blob([content], { type: 'text/csv;charset=utf-8' })
      : content;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Sanitize a user-facing string into something safe for a filename.
 */
export function sanitizeFilenameStem(input: string, fallback = 'download'): string {
  const s = (input || '').trim().replace(/[^A-Za-z0-9_\-]+/g, '_').replace(/^_+|_+$/g, '');
  return s.length > 0 ? s.slice(0, 80) : fallback;
}
